import { plural, t } from '@lingui/macro'

import { DocumentItem } from '@/modules/documents/components/document-item/DocumentItem'
import { MessageItem } from '@/modules/messages/components/message-item/MessageItem'
import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { RelatedList } from '@/modules/shared/components/related-list/RelatedList'
import { TaskItem } from '@/modules/task/components/task-item/TaskItem'

import type { Key, ReactNode } from 'react'
import type { useRelatedItems } from '../../hooks/useRelatedItems'

type RelatedItemsProps = ReturnType<typeof useRelatedItems>

/**
 * The tasks, messages and documents that belong to an agenda item, one group per kind. A view:
 * the page fetches them and decides what opening one does (see useRelatedItems).
 */
const RelatedItems = ({
  isLoading,
  tasks,
  messages,
  documents,
  onOpenTask,
  onOpenMessage,
  onOpenDocument,
}: RelatedItemsProps) => {
  if (isLoading) {
    return <AnimatedLoader small />
  }

  if (tasks.length === 0 && messages.length === 0 && documents.length === 0) {
    return null
  }

  return (
    <RelatedList>
      <RelatedGroup
        title={t({
          id: 'related-tasks.title',
          message: plural(tasks.length, {
            one: 'Bijhorende taak',
            other: 'Bijhorende taken',
          }),
        })}
        items={tasks}
        onOpen={onOpenTask}
        renderItem={(task) => <TaskItem task={task} />}
      />

      <RelatedGroup
        title={t({
          id: 'related-messages.title',
          message: plural(messages.length, {
            one: 'Bijhorend bericht',
            other: 'Bijhorende berichten',
          }),
        })}
        items={messages}
        onOpen={onOpenMessage}
        renderItem={(message) => <MessageItem message={message} />}
      />

      <RelatedGroup
        title={t({
          id: 'related-documents.title',
          message: plural(documents.length, {
            one: 'Bijhorend document',
            other: 'Bijhorende documenten',
          }),
        })}
        items={documents}
        onOpen={onOpenDocument}
        renderItem={(document) => <DocumentItem document={document} />}
      />
    </RelatedList>
  )
}

type RelatedGroupProps<T extends { id: Key }> = {
  title: string
  items: T[]
  onOpen: (item: T) => void
  renderItem: (item: T) => ReactNode
}

// One kind of item under its heading. A kind without items shows no group at all.
const RelatedGroup = <T extends { id: Key }>({ title, items, onOpen, renderItem }: RelatedGroupProps<T>) => {
  if (items.length === 0) {
    return null
  }

  return (
    <RelatedList.Group>
      <RelatedList.Title>{title}</RelatedList.Title>
      {items.map((item) => (
        <RelatedList.Row key={item.id} onClick={() => onOpen(item)}>
          {renderItem(item)}
        </RelatedList.Row>
      ))}
    </RelatedList.Group>
  )
}

export { RelatedItems }
